/**
 * Cart Recommendation Product - 处理购物车推荐中的产品选择和添加到购物车功能
 */
class CartRecommendationProduct extends HTMLElement {
  constructor() {
    super();

    this.productBlock = this.closest(".product-block");
    this.productId = this.dataset.productId;
    this.form = this.querySelector(".cart-add-form");
    this.variantInputs = this.querySelectorAll('input[name="id"]');
    this.optionSelectors = this.querySelectorAll(".option-selector");
    this.addToCartBtn = this.querySelector(".in-cart-add");

    // 获取产品的所有变体数据
    this.variantsData = JSON.parse(
      this.querySelector('[type="application/json"]')?.textContent || "[]",
    );

    this.init();
  }

  init() {
    // 监听选项变化
    this.optionSelectors.forEach((selector) => {
      // 这里是自定义组件 <custom-select>，所以不用 . ✅
      const customSelect = selector.querySelector("custom-select");
      const drawerEl = this.closest("cart-drawer");
      if (!drawerEl) return;

      if (customSelect) {
        const dropEl = customSelect.querySelector(".custom-select__listbox");

        if (!dropEl) return;

        // 箭头函数，绑定正确 this
        const setDropPosition = () => {
          const rect = customSelect.getBoundingClientRect();
          // cart-drawer 使用 transform 打开抽屉，会成为 fixed 子元素的定位上下文。
          // 必须每次都读取最新位置：初始化时抽屉通常还是关闭状态，缓存的 rect
          // 会导致下拉框在抽屉打开后产生很大的横向偏移。
          const drawerRect = drawerEl.getBoundingClientRect();
          dropEl.style.top = `${rect.bottom - drawerRect.top}px`;
          dropEl.style.left = `${rect.left - drawerRect.left}px`;
        };

        // 初始定位
        setDropPosition();

        // 窗口变化时重新定位，保存引用以便清理
        this._resizeHandler = setDropPosition;
        window.addEventListener("resize", this._resizeHandler);
        window.addEventListener("scroll", this._resizeHandler, true);
        customSelect.addEventListener("mousedown", setDropPosition);

        // 监听组件 change 事件
        customSelect.addEventListener(
          "change",
          this.handleOptionChange.bind(this),
        );
      }
    });

    // 加购表单
    if (this.form) {
      this.form.addEventListener("submit", this.handleAddToCart.bind(this));
    }
  }

  // 清理事件监听，防止内存泄漏
  disconnectedCallback() {
    if (this._resizeHandler) {
      window.removeEventListener("resize", this._resizeHandler);
      window.removeEventListener("scroll", this._resizeHandler, true);
    }
  }

  /**
   * 处理选项变化
   * @param {Event} evt - 事件对象
   */
  handleOptionChange(evt) {
    const selectedOptions = this.getSelectedOptions();
    const variant = this.getVariantFromOptions(selectedOptions);
    console.log("variant", variant);

    if (variant) {
      // 更新隐藏的变体ID输入框
      const variantInput = this.form.querySelector('input[name="id"]');
      if (variantInput) {
        variantInput.value = variant.id;
      }

      // 更新按钮的变体ID
      if (this.addToCartBtn) {
        this.addToCartBtn.dataset.variantId = variant.id;

        // 更新按钮状态
        if (variant.available) {
          this.addToCartBtn.removeAttribute("disabled");
          this.addToCartBtn.textContent =
            this.addToCartBtn.dataset.addToCartText || "Add to Cart";
        } else {
          this.addToCartBtn.setAttribute("disabled", "true");
          this.addToCartBtn.textContent = "Sold Out";
        }
      }
    } else {
      // 没有找到匹配的变体
      if (this.addToCartBtn) {
        this.addToCartBtn.setAttribute("disabled", "true");
        this.addToCartBtn.textContent = "Unavailable";
      }
    }
  }

  /**
   * 获取当前选择的选项
   * @returns {Array} 选项数组
   */
  getSelectedOptions() {
    const selectedOptions = [];

    this.optionSelectors.forEach((selector) => {
      const index = parseInt(selector.dataset.index, 10);
      const customSelect = selector.querySelector("custom-select");

      if (customSelect) {
        const selectedOption = customSelect.querySelector(
          '[aria-selected="true"]',
        );
        if (selectedOption) {
          selectedOptions[index] = selectedOption.dataset.value;
        } else {
          selectedOptions[index] = null;
        }
      } else {
        // 如果没有custom-select，则从原生select元素获取选中的值
        const nativeSelect = selector.querySelector("select");
        if (nativeSelect) {
          selectedOptions[index] = nativeSelect.value;
        } else {
          selectedOptions[index] = null;
        }
      }
    });

    console.log("options", selectedOptions);
    return selectedOptions;
  }

  /**
   * 根据选项获取对应的变体
   * @param {Array} options - 选项数组
   * @returns {Object|null} 变体对象
   */
  getVariantFromOptions(options) {
    if (!this.variantsData || this.variantsData.length === 0) {
      return null;
    }

    // 过滤掉空值后按位置匹配，避免稀疏数组导致的误匹配
    const validOptions = options.filter((opt) => opt != null);
    return (
      this.variantsData.find((variant) => {
        return validOptions.every((option, index) => {
          return option === variant.options[index];
        });
      }) || null
    );
  }

  /**
   * 处理添加到购物车
   * @param {Event} evt - 事件对象
   */
  async handleAddToCart(evt) {
    evt.preventDefault();
    if (
      this.addToCartBtn &&
      this.addToCartBtn.getAttribute("disabled") === "true"
    ) {
      return;
    }

    // 禁用按钮，防止重复提交
    if (this.addToCartBtn) {
      this.addToCartBtn.setAttribute("disabled", "true");
      this.addToCartBtn.classList.add("is-loading");
    }

    const formData = new FormData(this.form);
    console.log("Adding to cart:", formData);

    try {
      const response = await fetch(theme.routes.cartAdd, {
        method: "POST",
        body: formData,
        headers: {
          "X-Requested-With": "XMLHttpRequest",
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.description || data.message || "Error adding item to cart",
        );
      }

      // 刷新购物车抽屉
      if (document.querySelector(".template-cart")) {
        // Cart page
        const cartForm = document.querySelector("cart-form");
        if (cartForm && cartForm.refresh) {
          cartForm.refresh();
        }
      } else {
        const sectionsToUpdate = document.querySelectorAll("[data-section-id]");
        if (
          data.sections &&
          sectionsToUpdate.length === Object.keys(data.sections).length
        ) {
          sectionsToUpdate.forEach((el) => {
            el.updateFromCartChange &&
              el.updateFromCartChange(data.sections[el.dataset.sectionId]);
          });
        } else {
          this.dispatchEvent(
            new CustomEvent("on:cart:change", { bubbles: true }),
          );
        }
      }

      this.dispatchEvent(
        new CustomEvent("on:cart:add", {
          bubbles: true,
          detail: {
            variantId: data.variant_id,
          },
        }),
      );
    } catch (error) {
      console.error("Error adding to cart:", error);
    } finally {
      // 恢复按钮状态
      if (this.addToCartBtn) {
        this.addToCartBtn.removeAttribute("disabled");
        this.addToCartBtn.classList.remove("is-loading");
        // 恢复按钮文字，根据当前 variant 状态决定
        const currentVariant = this.getVariantFromOptions(
          this.getSelectedOptions(),
        );
        if (currentVariant && !currentVariant.available) {
          this.addToCartBtn.textContent = "Sold Out";
        } else {
          this.addToCartBtn.textContent =
            this.addToCartBtn.dataset.addToCartText || "Add to Cart";
        }
      }
    }
  }
}

customElements.define("cart-recommendation-product", CartRecommendationProduct);
